import Vue from 'vue';
import axios from 'axios';
import config from '@/config/constants.js';
import { firestore } from "@/config/firebase";
import { retrieveUser } from "@/util/users";
import {
  getFormattedMessage,
  getFormattedConversation,
  searchContacts,
  searchConversations,
  searchMessages,
  getFormattedMessageForSearch,
  getAllMessagesForConversations,
  getAllUsersForTenant
} from "@/util/chat";
import { loadImageFromVideo, loadImageFromPdf, isVideo, isImage, isPdf, uploadFileToStorage } from "@/util/video_utils_v2";
import { v4 as uuid } from 'uuid';
import moment from 'moment';

const database = firestore;

// Resolve the current user's key in a conversation's unread_counters map. The
// key is usually owner_id, but falls back to the tenant-scoped user id when
// they differ. Single source of truth (previously duplicated in 4 places, which
// could disagree and produce mismatched badges).
function resolveCounterKey(state, conv) {
  if (!conv) return state.owner_id;
  if (conv.unread_counters && conv.unread_counters[state.owner_id] !== undefined) return state.owner_id;
  const prefix = '/tenants/' + state.tenant_id + '/users/';
  const otherIds = (conv.participants || []).filter(p => p.user_id !== state.owner_id).map(p => p.user_id);
  const ids = (conv.participant_ids || []).map(p => p.replace(prefix, ''));
  const found = ids.find(id => !otherIds.includes(id));
  return found || state.owner_id;
}

// Throttle the (heavy: transaction + 50-doc read + batch) unread-counter write
// so a burst of inbound messages / focus events coalesces into one write.
const _unreadThrottle = {};

let _uxDocsSaveTimer = null;
let _pendingUxDocs = null;
function saveUxDocsDebounced(uxDocs) {
  // Never save empty uxDocs — it corrupts the cache and causes blank screens
  if (!uxDocs || Object.keys(uxDocs).length === 0) return;
  _pendingUxDocs = uxDocs;
  if (_uxDocsSaveTimer) clearTimeout(_uxDocsSaveTimer);
  _uxDocsSaveTimer = setTimeout(() => {
    try {
      localStorage.setItem("uxDocs", JSON.stringify(uxDocs));
      _pendingUxDocs = null;
    } catch (e) {
      console.warn('Failed to save uxDocs to localStorage:', e);
    }
  }, 300);
}

// Flush pending uxDocs on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (_pendingUxDocs) {
      try {
        localStorage.setItem("uxDocs", JSON.stringify(_pendingUxDocs));
      } catch (e) { /* ignore */ }
    }
  });
}

function checkUxDocs() {
  let docs = {};
  try {
    if (localStorage.getItem('uxDocs')) {
      docs = JSON.parse(localStorage.getItem('uxDocs'))
    }
    // eslint-disable-next-line no-empty
  } catch (error) {
    localStorage.removeItem('uxDocs');
    docs = {};
  }
  return docs;
}

export default {
  namespaced: true,
  state: {
    preloading: true,
    status: '',
    token: (localStorage.getItem('WA_id') && localStorage.getItem('WA_id') !== 'null') ? localStorage.getItem('WA_id') : null,
    chatToken: (localStorage.getItem('token') && localStorage.getItem('token') !== 'null') ? localStorage.getItem('token') : null,
    chatCustomToken: (localStorage.getItem('customToken') && localStorage.getItem('customToken') !== 'null') ? localStorage.getItem('customToken') : null,
    conversations: [],
    messages: [],
    all_messages: [],
    files: [],
    dataMounths: [],
    mediaFilesChat: [],
    unread_message: [],
    users: [],
    uxDocs: checkUxDocs(),
    status_msg: '',
    status_users: '',
    status_chat: '',
    status_chat_get: '',
    status_contact: '',
    status_conversations: '',
    status_notification: '',
    status_retrieve: '',
    status_file: '',
    logged: {},
    tenant_id: null,
    tenant_company_name: null,
    owner_id: null, //user_global
    user_doc: null, //user_details
    device_id: null,
    search_results: [],
    search_results_files: [],
    chat_ids: [],
    device_token: '',
    countrySelected: ['ca', 'us'],
    search_status: "",
    initError: false,
    chats_without_messages: [],
    users_in_chats_without_messages: [],
    address_results: [],
    messagesCache: {},
    lastRequestedChatId: null
  },
  mutations: {
    SET_USER_DATA(state, payload) {
      state.tenant_id = payload.tenant_id;
      state.owner_id = payload.owner_id;
      state.device_id = payload.device_id;
    },
    customToken(state, data) {
      state.chatToken = data.token;
      state.chatCustomToken = data.customToken;
      localStorage.setItem("token", state.chatToken);
      localStorage.setItem("customToken", state.chatCustomToken);
    },
    search_status(state, payload) {
      state.search_status = payload;
    },
    set_location(state, country) {
      state.countrySelected = country;
    },
    add_all_messages(state, message) {
      const index = state.all_messages.findIndex(x => x.message_id === message.message_id);
      if (index !== -1) {
        for (let j in message) {
          state.all_messages[index][j] = message[j];
        }
      } else {
        if (message.text) {
          state.all_messages.unshift({
            ...message,
            message_id: message.id,
            text: message.text,
            description: message.description,
            search: message.description ? message.description.toLowerCase() : message.text.toLowerCase(),
            creation_date: message.creation_date ? message.creation_date.toDate() : new Date()
          });
        }
      }
    },
    search_results(state, payload) {
      state.search_results = payload;
    },
    search_results_append(state, payload) {
      state.search_results = state.search_results.concat(payload);
    },
    files(state, payload) {
      state.files = payload;
    },
    search_results_files(state, payload) {
      state.search_results_files = payload;
    },
    all_messages(state, payload) {
      state.all_messages = payload;
    },
    set_chat_ids(state, id) {
      state.chat_ids.push(id);
    },
    setUxDocs(state, docs) {
      state.uxDocs = docs;
      saveUxDocsDebounced(state.uxDocs);
    },
    preloading(state, payload) {
      state.preloading = payload;
    },
    initError(state, payload) {
      state.initError = payload;
    },
    owner_id(state, id) {
      state.owner_id = id;
      if (id) {
        localStorage.setItem("owner_id", id);
      } else {
        localStorage.removeItem("owner_id");
      }
    },
    device_id(state, id) {
      state.device_id = id;
      if (id) localStorage.setItem('device_id', id); else localStorage.removeItem('device_id');
    },
    user_doc(state, id) {
      state.user_doc = id;
      if (id) localStorage.setItem('user_doc', id); else localStorage.removeItem('user_doc');
    },
    tenant(state, id) {
      state.tenant_id = id;
      if (id) localStorage.setItem('tenant_id', id); else localStorage.removeItem('tenant_id');
    },
    tenant_company_name(state, name) {
      state.tenant_company_name = name;
    },
    loading(state) {
      state.status = 'loading';
    },
    status(state, status) {
      state.status = status;
    },
    status_retrieve(state, status) {
      state.status_retrieve = status;
    },
    status_file(state, status) {
      state.status_file = status;
    },
    status_notification(state, status) {
      state.status_notification = status;
    },
    status_msg(state, status) {
      state.status_msg = status;
    },
    status_chat(state, status) {
      state.status_chat = status;
    },
    status_chat_get(state, status) {
      state.status_chat_get = status;
    },
    status_contact(state, status) {
      state.status_contact = status;
    },
    status_users(state, status) {
      state.status_users = status;
    },
    status_conversations(state, status) {
      state.status_conversations = status;
    },
    conversations(state, payload) {
      state.conversations = payload;
    },
    addToken(state, data) {
      const response = data.response;
      state.token = response['tokent'] || response['token'];
      if (state.token) {
        localStorage.setItem('WA_id', state.token);
      } else {
        localStorage.removeItem('WA_id');
      }
    },
    unreadMessages(state, data) {
      var index = state.unread_message.map((o) => o.chat_id).indexOf(data.chat_id);
      if (index !== -1) {
        if (data.reset) {
          state.unread_message[index].count = 0;
        } else {
          state.unread_message[index].count = state.unread_message[index].count + 1;
        }
      } else {
        state.unread_message.push({
          chat_id: data.chat_id,
          count: 0
        });
      }
    },
    setUsers(state, users) {
      state.users = users;
    },
    addUsers(state) {
      state.users = [];

      for (let user_id in state.uxDocs) {
        let user = state.uxDocs[user_id];
        if (user.owner_id !== state.owner_id && !user.disabled_by_agent_owner && user.status === 'Active') {
          state.users.push(user);
        }
      }

      state.users = state.users.sort(function (a, b) {
        return a.user_role > b.user_role ? 1 : -1;
      });
    },
    deleteConversation(state, chat_id) {
      let index = state.conversations.findIndex(x => x.chat_id === chat_id);
      if (index !== -1) {
        state.conversations.splice(index, 1);
      }
    },
    updateConversation(state, conversation) {
      const conversationIndex = state.conversations.findIndex(x => x.chat_id === conversation.chat_id);
      if (conversationIndex !== -1) {
        let data = getFormattedConversation(conversation, state, conversationIndex);
        for (const i in data) {
          Vue.set(state.conversations[conversationIndex], i, data[i]);
        }

        state.conversations = state.conversations.sort(function (a, b) {
          const dateA = a.last_message.date ? new Date(a.last_message.date).getTime() : 0;
          const dateB = b.last_message.date ? new Date(b.last_message.date).getTime() : 0;
          return dateA < dateB ? 1 : (dateA > dateB ? -1 : 0);
        });

        let conversationsActive = state.conversations.filter(x => x.status !== "inactive");
        let conversationsInactive = state.conversations.filter(x => x.status === "inactive");
        state.conversations = conversationsActive.concat(conversationsInactive);
      }
    },
    addConversation(state, conversation) {
      if (conversation.last_message === undefined && !conversation.title && !state.chats_without_messages.includes(conversation.chat_id)) {
        state.chats_without_messages.push(conversation.chat_id);
        for (const participant of (conversation.participants || [])) {
          if (participant.user_id !== state.owner_id) {
            state.users_in_chats_without_messages.push(participant.user_id)
          }
        }
      }

      const conversationIndex = state.conversations.findIndex(x => x.chat_id === conversation.chat_id);
      if (state.logged && ["owner", "agent", "vendor", "employee", "super_user"].includes(state.logged.user_role) && !conversation.chat_replied && conversation.is_autogenerated === true && conversation.created_by === state.owner_id) {
        return;
      }

      let data = getFormattedConversation(conversation, state, conversationIndex);

      if (conversationIndex !== -1) {
        for (const i in data) {
          Vue.set(state.conversations[conversationIndex], i, data[i]);
        }
      } else {
        state.conversations.push(data);
      }

      state.conversations = state.conversations.sort(function (a, b) {
        const dateA = a.last_message.date ? new Date(a.last_message.date).getTime() : 0;
        const dateB = b.last_message.date ? new Date(b.last_message.date).getTime() : 0;
        return dateA < dateB ? 1 : (dateA > dateB ? -1 : 0);
      });

      let conversationsActive = state.conversations.filter(x => x.status !== "inactive");
      let conversationsInactive = state.conversations.filter(x => x.status === "inactive");
      state.conversations = conversationsActive.concat(conversationsInactive);
      state.status_conversations = '';
    },
    addConversationBulk(state, conversations) {
      for (let conversation of conversations) {
        if (conversation.last_message === undefined && !conversation.title && !state.chats_without_messages.includes(conversation.chat_id)) {
          state.chats_without_messages.push(conversation.chat_id);
          for (const participant of (conversation.participants || [])) {
            if (participant.user_id !== state.owner_id) {
              state.users_in_chats_without_messages.push(participant.user_id)
            }
          }
        }

        const conversationIndex = state.conversations.findIndex(x => x.chat_id === conversation.chat_id);
        if (state.logged && ["owner", "agent", "vendor", "employee", "super_user"].includes(state.logged.user_role) && !conversation.chat_replied && conversation.is_autogenerated === true && conversation.created_by === state.owner_id) {
          continue;
        }

        let data = getFormattedConversation(conversation, state, conversationIndex);

        if (conversationIndex !== -1) {
          for (const i in data) {
            Vue.set(state.conversations[conversationIndex], i, data[i]);
          }
        } else {
          state.conversations.push(data);
        }
      }

      state.conversations = state.conversations.sort(function (a, b) {
        const dateA = a.last_message.date ? new Date(a.last_message.date).getTime() : 0;
        const dateB = b.last_message.date ? new Date(b.last_message.date).getTime() : 0;
        return dateA < dateB ? 1 : (dateA > dateB ? -1 : 0);
      });

      let conversationsActive = state.conversations.filter(x => x.status !== "inactive");
      let conversationsInactive = state.conversations.filter(x => x.status === "inactive");
      state.conversations = conversationsActive.concat(conversationsInactive);
      state.status_conversations = '';
    },
    logged(state, data) {
      state.logged = data;
    },
    updateLogged(state, data) {
      for (const i in data) {
        state.logged[i] = data[i];
      }
    }
    ,
    set_chat_media_files(state, docs) {
      state.mediaFilesChat = [];

      for (const data of docs) {
        let file = data.data();
        file.id = data.id;

        if (isVideo(file)) {
          const chatId = file.chat_id;
          loadImageFromVideo(file.url).then(videoPreview => {
            // Only update if still viewing the same chat
            const currentChatId = state.messages.length > 0 ? state.messages[0].chat_id : null;
            if (currentChatId !== chatId) return;
            for (let k = 0; k < state.messages.length; k++) {
              if (state.messages[k].message_id === file.message_id) {
                Vue.set(state.messages[k], 'media_preview_url', videoPreview);
              }
            }
          }).catch(() => {});
        } else if (isImage(file)) {
          for (let k = 0; k < state.messages.length; k++) {
            if (state.messages[k].message_id === file.message_id) {
              Vue.set(state.messages[k], 'media_preview_url', file.url);
            }
          }
        }

        state.mediaFilesChat.push(file);
      }
    },
    add_chat_media_file(state, docs) {
      for (const data of docs) {
        let file = data.data();
        if (file) {
          file.id = data.id;

          if (isVideo(file)) {
            loadImageFromVideo(file.url).then(videoPreview => {
              for (const message of state.messages) {
                if (message.message_id === file.message_id) {
                  message.media_preview_url = videoPreview;
                }
              }
            });
          } else if (isImage(file)) {
            for (const message of state.messages) {
              if (message.message_id === file.message_id) {
                message.media_preview_url = file.url;
              }
            }
          }

          state.mediaFilesChat.push(file);
        }
      }
    },
    add_chat_message_attribute(state, payload) {
      const messageIndex = state.messages.findIndex(x => x.message_id === payload.message_id);
      if (messageIndex !== -1) {
        for (const i in payload.data) {
          state.messages[messageIndex][i] = payload.data[i];
        }
      }
    },
    remove_message(state, message_id) {
      const index = state.messages.findIndex(x => x.message_id === message_id);
      if (index !== -1) {
        state.messages.splice(index, 1);
      }

      state.messages = state.messages.sort(function (a, b) {
        var dateA = new Date(a.creation_date).getTime();
        var dateB = new Date(b.creation_date).getTime();
        return dateA > dateB ? 1 : -1;
      });
    },
    async addMessage(state, data) {
      if (data.auth_user) {
        let auth_user = data.auth_user;
        let message = data.content;

        let index = state.messages.findIndex(x =>
          (x.message_id && x.message_id === message.message_id) ||
          (x.message_identifier && x.message_identifier === message.message_identifier) ||
          (x.message_identifier && message.message_id && x.message_identifier === message.message_id) ||
          (x.message_id && message.message_identifier && x.message_id === message.message_identifier)
        );

        let formatted_message = getFormattedMessage(message, auth_user);
        if (index !== -1) {
          for (const i in formatted_message) {
            if (!["message_replied_data"].includes(i)) {
              Vue.set(state.messages[index], i, formatted_message[i]);
            }
          }
          state.messages[index].sending = false;
        } else {
          const msgTime = new Date(formatted_message.creation_date).getTime();
          let insertIdx = state.messages.length;
          for (let k = state.messages.length - 1; k >= 0; k--) {
            if (new Date(state.messages[k].creation_date).getTime() <= msgTime) {
              insertIdx = k + 1;
              break;
            }
            if (k === 0) insertIdx = 0;
          }
          state.messages.splice(insertIdx, 0, formatted_message);
          for (const i in state.conversations) {
            if (state.conversations[i].chat_id === formatted_message.chat_id) {
              // Increment unread counter for incoming messages (not sent by me)
              if (!formatted_message.auth_user) {
                const counters = state.conversations[i].unread_counters || {};
                const myKey = resolveCounterKey(state, state.conversations[i]);
                counters[myKey] = (counters[myKey] || 0) + 1;
                state.conversations[i].unread_counters = counters;
                state.conversations[i].unread_messages_count = counters[myKey];
              }
              state.conversations[i].forceRender += 1;
              break;
            }
          }
        }
      }
    }
    ,
    async addMessages(state, data) {
      if (data.empty) {
        state.messages = [];
      }
      if (data.auth_user) {
        let auth_user = data.auth_user;
        let messages = data.content;
        for (const i in messages) {
          const message = messages[i];
          state.messages.push(getFormattedMessage(message, auth_user));
        }

        state.messages = state.messages
          .sort((a, b) => new Date(a.creation_date).getTime() > new Date(b.creation_date).getTime() ? 1 : -1);
      }
    },
    async addFiles(state, data) {
      if (data.files.length === 0) {
        state.files = [];
      } else {
        let current_month = moment(data.files[0].creation_date.toDate()).month();
        state.dataMounths = [{
          date: moment(data.files[0].creation_date.toDate()).format("MMMM YYYY"),
          month: current_month
        }];

        if (data.append === true) {
          state.files = state.files.concat(data.files);
        } else {
          state.files = data.files;
        }
        for (const prop in data.files) {
          let file = data.files[prop];
          file.creation_date = file.creation_date.toDate();

          if (isVideo(file)) {
            loadImageFromVideo(file.url).then(videoPreview => {
              file.preview_url = videoPreview;
            })
          }

          if (isPdf(file) && !file.preview_url) {
            loadImageFromPdf(file.url).then(pdfPreview => {
              file.preview_url = pdfPreview;
            })
          }

          if (moment(file.creation_date).month() != current_month) {
            current_month = moment(file.creation_date).month();
            state.dataMounths.push({ date: moment(file.creation_date).format("MMMM YYYY"), month: current_month });
          }
        }

        state.files = state.files.sort(function (a, b) {
          var dateA = new Date(a.creation_date).getTime();
          var dateB = new Date(b.creation_date).getTime();
          return dateA < dateB ? 1 : -1;
        });
      }
    }
    ,
    device_token(state, payload) {
      state.device_token = payload;
    }
    ,
    async updateUserGlobal(state, doc) {
      const data = doc.data();
      if (!state.uxDocs[doc.id]) {
        return;
      }
      for (let i in data) {
        Vue.set(state.uxDocs[doc.id], i, data[i]);
        Vue.set(state.logged, i, data[i]);
      }

      const tenantEntry = (data.tenants_list || []).find(x => x.tenant_id === state.tenant_id);
      let updateConversations = false;
      if (
        state.uxDocs[doc.id]["text"] !== (data.first_name + " " + data.last_name).toLowerCase() ||
        (tenantEntry && state.uxDocs[doc.id]["status"] !== tenantEntry.status)
      ) {
        updateConversations = true;
      }

      Vue.set(state.uxDocs[doc.id], "id", doc.id);
      Vue.set(state.uxDocs[doc.id], "owner_id", doc.id);
      Vue.set(state.uxDocs[doc.id], "global", true);
      Vue.set(state.uxDocs[doc.id], "text", (data.first_name + " " + data.last_name).toLowerCase());
      Vue.set(state.uxDocs[doc.id], "user_role", tenantEntry ? tenantEntry.role : state.uxDocs[doc.id]["user_role"]);
      Vue.set(state.uxDocs[doc.id], "status", tenantEntry ? tenantEntry.status : state.uxDocs[doc.id]["status"]);

      switch (state.uxDocs[doc.id]["user_role"]) {
        case "client": {
          state.uxDocs[doc.id]["category"] = "contacts";
          break;
        }
        case "vendor": {
          state.uxDocs[doc.id]["category"] = "vendors";
          break;
        }
        case "agent": {
          state.uxDocs[doc.id]["category"] = "agents";
          break;
        }
      }

      if (updateConversations) {
        for (const conversation of state.conversations) {
          if (conversation.participants.find(x => x.user_id === doc.id)) {
            conversation.forceRender += 1;
          }
        }
      }
      saveUxDocsDebounced(state.uxDocs);
    },
    async updateUserInner(state, doc) {
      const data = doc.data();
      if (!state.uxDocs[data.owner_id]) {
        return;
      }
      state.uxDocs[data.owner_id]["is_active"] = data.is_active;
      state.uxDocs[data.owner_id]["first_login"] = data.first_login;
      state.uxDocs[data.owner_id]["has_logged_into_app"] = data.has_logged_into_app;
      // Presence: last_connection (Unix seconds; 0 = "Active now") is written
      // onto the tenant user doc by the presence endpoint / mobile clients.
      // Vue.set because this key isn't present on the global users doc, so a
      // plain assignment wouldn't be reactive and the chat header would never
      // pick up presence changes.
      Vue.set(state.uxDocs[data.owner_id], "last_connection", data.last_connection !== undefined ? data.last_connection : null);

      saveUxDocsDebounced(state.uxDocs);
    },
    async forceRenderConversations(state) {
      for (const conversation of state.conversations) {
        conversation.forceRender += 1;
      }
    }
  },

  actions: {
    dev_set_token({ commit }) {
      const data = {
        token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiOG8yWEl1RHk5Q2xoQ2M0RGR2cDIiLCJ0ZW5hbnRfaWQiOiJ1SURvd04wZzFzRUFJOHJvTlZVUCIsInVzZXJfcm9sZSI6bnVsbCwiaWF0IjoxNzY4NTcwMzc1LCJleHAiOjE3Njg1NzAzNzV9.jiVz8qIFlJp9odz68Ya1RHDg4wN9jBVqyG1tLTxAvxc",
        customToken: "eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.eyJhdWQiOiJodHRwczovL2lkZW50aXR5dG9vbGtpdC5nb29nbGVhcGlzLmNvbS9nb29nbGUuaWRlbnRpdHkuaWRlbnRpdHl0b29sa2l0LnYxLklkZW50aXR5VG9vbGtpdCIsImlhdCI6MTc2ODU3MDM3NSwiZXhwIjoxNzY4NTczOTc1LCJpc3MiOiJmaXJlYmFzZS1hdXRoLWVtdWxhdG9yQGV4YW1wbGUuY29tIiwic3ViIjoiZmlyZWJhc2UtYXV0aC1lbXVsYXRvckBleGFtcGxlLmNvbSIsInVpZCI6IjF1SUpOMnJZVUxCWEFTcjNNNE5TUXFOUVJBQ08ifQ."
      };

      commit("customToken", data);

      const ownerId = "8o2XIuDy9ClhCc4Ddvp2";
      commit("owner_id", ownerId);
      const tenantId = "uIDowN0g1sEAI8roNVUP";
      commit("tenant", tenantId);
      localStorage.setItem("owner_id", ownerId);
      localStorage.setItem("WA_id", ownerId);
    },
    //  eslint-disable-next-line no-empty-pattern
    change_event_rvsp: ({ }, payload) => {
      return new Promise((resolve, reject) => {
        axios.post(config.api_url + "change_event_rvsp", {
          chat_id: payload.message.chat_id,
          message_id: payload.message.message_id,
          status: payload.status
        }).then(() => {
          resolve();
        })
          .catch((err) => {
            reject(err.response);
          });
      });
    },
    // eslint-disable-next-line no-empty-pattern
    cancel_event: async ({ }, message) => {
      return new Promise((resolve, reject) => {
        axios.post(config.api_url + "cancel_event", {
          chat_id: message.chat_id,
          message_id: message.message_id
        }).then(() => {
          resolve();
        })
          .catch((err) => {
            reject(err.response);
          });
      });
    },
    get_users_global: ({ commit, state }) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise((resolve, reject) => {
        database.collection("tenants")
          .doc(state.tenant_id)
          .collection("users")
          .get().then(queryUserDetails => {


            if (!queryUserDetails.docs.length) {
              resolve();
              return;
            }

            const ownerIds = queryUserDetails.docs
              .filter(x => x.data().owner_id)
              .map(x => x.data().owner_id);

            getAllUsersForTenant(ownerIds).then(queryUsers => {
                let usersDetails = {};
                for (const user of queryUserDetails.docs) {
                  const userData = user.data();
                  usersDetails[userData.owner_id] = { ...userData, id: user.id };
                }

                let arrayUsers = {};
                for (const user of queryUsers) {
                  let data = user.data();
                  if (!data.tenants_list) {
                    continue;
                  }
                  const tenantEntry = data.tenants_list.find(x => x.tenant_id === state.tenant_id);
                  if (!tenantEntry) {
                    continue;
                  }
                  const nonOwnerEntry = data.tenants_list.find(x => x.tenant_id === state.tenant_id && x.role !== "owner");
                  // Use tenant-scoped user doc status (authoritative) over global tenants_list status (stale)
                  const tenantUserDoc = usersDetails[user.id];
                  let userStatus = tenantEntry.status || "inactive";
                  if (tenantUserDoc && tenantUserDoc.status) {
                    userStatus = tenantUserDoc.status;
                  }

                  data = {
                    ...data,
                    id: user.id,
                    owner_id: user.id,
                    global: true,
                    text: (data.first_name + " " + data.last_name).toLowerCase(),
                    user_role: nonOwnerEntry ? nonOwnerEntry.role : "",
                    status: userStatus,
                  };

                  if (tenantUserDoc) {
                    data.first_login = tenantUserDoc.first_login;
                    data.has_logged_into_app = tenantUserDoc.has_logged_into_app;
                    data.is_active = tenantUserDoc.is_active;
                    data.invited_by_agent = tenantUserDoc.invited_by_agent;
                    data.disabled_by_agent_owner = tenantUserDoc.disabled_by_agent_owner;
                  }

                  switch (data.user_role) {
                    case "agent": {
                      data.category = "agents";
                      data.categoryOrder = 1;
                      break;
                    }
                    case "vendor": {
                      data.category = "vendors";
                      data.categoryOrder = 2;
                      break;
                    }
                    case "employee": {
                      data.category = "vendors";
                      data.categoryOrder = 2;
                      break;
                    }
                    case "client": {
                      data.category = "clients";
                      data.categoryOrder = 3;
                      break;
                    }
                    default: {
                      data.category = "other";
                      data.categoryOrder = 4;
                      break;
                    }
                  }

                  arrayUsers[data.id] = data;
                }
                commit("setUxDocs", arrayUsers);
                resolve();
              }).catch(error => {
                console.error(error);
                reject(error)
              });
          }).catch(error => {
            console.error(error);
            reject(error)
          });
      });
    },
    web_token: function ({ state, commit }) {
      return new Promise((resolve, reject) => {

        // // ?? SOLO DEV / WEB
        // if (
        //   process.env.NODE_ENV === "development" &&
        //   !localStorage.getItem("auth_id")
        // ) {
        //   // ?? inyectamos tokens sin QR
        //   dispatch("dev_set_token");

        //   resolve({
        //     token: localStorage.getItem("token"),
        //     customToken: localStorage.getItem("customToken")
        //   });

        //   return;
        // }

        // ?? Flujo normal (produccin / mvil)
        if (!localStorage.getItem("auth_id")) {
          axios
            .post(config.api_url + 'web_token')
            .then((response) => {
              commit('addToken', { response: response.data });
              resolve(response);
            })
            .catch((err) => {
              reject(err?.response?.message || err);
            });
        } else {
          resolve({ token: state.token });
        }
      });
    },

    get_location: function () {
      return new Promise((resolve, reject) => {
        axios
          .get(config.api_url + 'get_location')
          .then((response) => {
            resolve(response.data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    // eslint-disable-next-line no-empty-pattern
    generate_firebase_token: function ({ commit }, params) {
      return new Promise((resolve, reject) => {
        // Two callers. The QR page is pre-auth and has no session — the backend
        // authorizes it on the freshly scanned pairing. SwitchAccount is already
        // signed in and re-mints for another tenant, which the backend
        // authorizes on the SESSION because the original pairing has long since
        // expired. Send that session explicitly instead of leaning on the
        // ambient axios default: the QR login path never writes `auth_token`,
        // and `auth_id` is only re-attached from inside the chat view.
        let requestConfig;
        try {
          const session = localStorage.getItem('auth_id') || localStorage.getItem('auth_token');
          if (session) requestConfig = { headers: { Authorization: session } };
        } catch (e) {
          console.warn('[Chat] localStorage unavailable for auth header:', e);
        }

        axios
          .post(config.api_url + 'web/generateToken', params, requestConfig)
          .then((response) => {
            commit("customToken", response.data);
            resolve(response.data);
          })
          .catch((error) => {
            console.error('[Chat] generate_firebase_token failed:', error);
            reject(error);
          });
      });
    },
    search_address: ({ state }, payload) => {
      return new Promise((resolve, reject) => {
        let query = database
          .collection("tenants")
          .doc(state.tenant_id)
          .collection("chats")
          .doc(payload.chat_id)
          .collection("messages")
          .where("type", "in", ["address", "event"])
          .orderBy("creation_date", "desc");
        if (payload.startAt) {
          query = query.startAt(payload.startAt);
        }
        query
          .get()
          .then(query_global => resolve(query_global.docs))
          .catch(error => reject(error));
      }
      );
    },
    search: ({ state, commit, dispatch }, payload) => {
      commit("search_status", "searching");
      if (window.search_status !== 'canceled') {
        commit("search_results", []);
      }

      let chats_groups = [];
      for (const conv of state.conversations) {
        if (conv.is_group) {
          chats_groups.push(conv.chat_id);
        }
      }
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async resolve => {
        const search = payload.search.toLowerCase();
        window.search_text = search;
        let search_results = [];
        if (window.search_status !== 'canceled') {
          search_results = search_results.concat(await searchContacts(Object.values(state.uxDocs), state.owner_id, search, state));
        }

        if (window.search_status !== 'canceled') {
          search_results = search_results.concat(await searchConversations(
            state.conversations,
            search_results.map(x => `/tenants/${state.tenant_id}/users/${x.owner_id}`),
            search));
        }

        let messages_result = [];

        if (window.search_status !== 'canceled') {
          if (payload.messages === undefined || payload.messages === true) {
            dispatch("load_all_messages", search).then(async all_messages => {
              if (window.search_status !== 'canceled') {
                commit("search_results_append", await searchMessages(all_messages, search));
              }
            });
          }
        }

        for (const result of search_results) {
          if (result.category === 'Contacts') {
            for (const message of messages_result) {
              if (result.owner_id === message.owner_id && !chats_groups.includes(message.chat_id)) {
                result.chat_id = message.chat_id
              }
            }

          }
        }
        commit("search_results", search_results);
        commit("search_status", "");
        resolve();
      });
    },
    searchFiles: async ({ state, commit }, payload) => {
      const search = payload.search.toLowerCase();
      let search_results = [];

      for (let i in state.uxDocs) {
        if (state.uxDocs[i].first_name.toLowerCase().indexOf(search) !== -1 || state.uxDocs[i].last_name.toLowerCase().indexOf(search) !== -1) {
          search_results.push({
            ...state.uxDocs[i]
          })
        }
      }
      commit("search_results_files", search_results);
    },
    // load_all_messages: async ({commit, state}, search) => {
    //   return new Promise(resolve => {
    //     let all_messages = [];
    //     const conversation_ids = state.conversations.map(x => x.chat_id);
    //     getAllMessagesForConversations(conversation_ids, state.tenant_id, state.logged.owner_id, search).then(allUserMessagesRelated => {
    //       console.log(conversation_ids);
    //       for (const x of allUserMessagesRelated) {
    //         let message = x.data();
    //         message.id = x.id;
    //         console.log(message);
    //         let formattedMessage = getFormattedMessageForSearch(conversation_ids, message);
    //         if (formattedMessage) {
    //           all_messages.push(formattedMessage);
    //         }
    //       }
    //
    //       all_messages = all_messages.sort(function (a, b) {
    //         const dateA = a.creation_date ? a.creation_date.getTime() : 0;
    //         const dateB = b.creation_date ? b.creation_date.getTime() : 0;
    //         return dateA < dateB ? 1 : (dateA > dateB ? -1 : 0);
    //       });
    //       commit("all_messages", all_messages);
    //       resolve(all_messages);
    //     });
    //   });
    // },
    load_all_messages: async ({ commit, state }, search) => {
      const all_messages = [];
      const conversation_ids = state.conversations.map(c => c.chat_id);

      try {
        // Traemos todos los mensajes relacionados con el usuario
        const allUserMessagesRelated = await getAllMessagesForConversations(
          conversation_ids,
          state.logged.owner_id,
          search,
          state.tenant_id
        );

        // Filtramos y formateamos
        allUserMessagesRelated.forEach(doc => {
          const message = doc.data();
          message.id = doc.id;

          // Filtrado de bsqueda por texto (insensible a maysculas/minsculas)
          if (message.text && message.text.toLowerCase().includes(search.toLowerCase())) {
            const formattedMessage = getFormattedMessageForSearch(conversation_ids, message);
            if (formattedMessage) {
              all_messages.push(formattedMessage);
            }
          }
        });

        // Ordenamos por fecha descendente
        all_messages.sort((a, b) => {
          const dateA = a.creation_date ? a.creation_date.getTime() : 0;
          const dateB = b.creation_date ? b.creation_date.getTime() : 0;
          return dateB - dateA;
        });

        // Guardamos en Vuex
        commit("all_messages", all_messages);
        return all_messages;

      } catch (error) {
        console.error("Error loading messages:", error);
        return [];
      }
    },

    logout: function ({ commit, state }) {
      state.conversations = [];
      state.search_results = [];
      state.messagesCache = {};
      state.messages = [];

      return new Promise(resolve => {
        commit('addToken', { response: { tokent: null } });
        axios.post(config.api_url + 'logout', {
          device_token: state.device_token,
          device_id: state.device_id
        });
        commit('conversations', []);
        commit('setUxDocs', {});
        commit('owner_id', null);
        commit('search_results', []);
        commit('user_doc', null);
        commit('device_id', null);
        commit('tenant_company_name', null);
        commit('logged', '');
        commit('customToken', { token: "", customToken: "" });
        localStorage.removeItem("token");
        localStorage.removeItem("customToken");
        localStorage.removeItem("auth_id");
        localStorage.removeItem("WA_id");
        localStorage.removeItem("uxDocs");
        localStorage.removeItem("tenant_id");
        localStorage.removeItem("user_doc");
        localStorage.removeItem("device_id");
        resolve();
      });
    },
    simple_logout: function ({ commit, state }) {
      return new Promise(resolve => {
        state.messagesCache = {};
        state.messages = [];
        commit('conversations', []);
        commit('setUxDocs', {});
        commit('owner_id', null);
        commit('search_results', []);
        commit('user_doc', null);
        commit('device_id', null);
        commit('tenant_company_name', null);
        commit('logged', '');
        commit('customToken', { token: "", customToken: "" });
        localStorage.removeItem("token");
        localStorage.removeItem("customToken");
        localStorage.removeItem("auth_id");
        localStorage.removeItem("WA_id");
        localStorage.removeItem("uxDocs");
        localStorage.removeItem("tenant_id");
        localStorage.removeItem("user_doc");
        localStorage.removeItem("device_id");
        resolve();
      });
    },
    older_messages: ({ commit, state, dispatch }, info) => {
      return new Promise((resolve, reject) => {
        if (!info || !info.top_date || !info.chat_id) { resolve(); return; }
        let path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
        try {
          const limit = info.limit === undefined ? 50 : info.limit;
          let ref = database
            .collection("tenants")
            .doc(state.tenant_id)
            .collection("chats")
            .doc(info.chat_id)
            .collection('messages')
            .where('creation_date', '<', info.top_date)
            .orderBy('creation_date', 'desc');

          const chat = state.conversations.find(x => x.chat_id === info.chat_id);
          if (chat && chat.deleted && chat.deleted[state.owner_id]) {
            ref = ref.where('creation_date', '>', new Date(chat.deleted[state.owner_id]));
          }

          if (limit) {
            ref = ref.limit(limit);
          }

          ref.get().then(async (collections) => {
            let messages = [];
            for (const collection of collections.docs) {
              let xm = collection.data();
              xm.message_id = collection.id;

              let senderData = xm.sender_id.split("/");
              let sender = retrieveUser({ owner_id: senderData[4] }, state.uxDocs);
              if (sender) {
                xm.owner_id = sender.owner_id;
                xm.user_role = sender.user_role;
                xm.first_name = sender.first_name;
                xm.last_name = sender.last_name;
              }

              if (xm.message_replied) {
                dispatch('findMessage', xm);
              }

              messages.push(xm);
            }

            commit('addMessages', {
              auth_user: path,
              content: messages,
              empty: !limit ? true : false
            });
            dispatch("pull_media", info);
            resolve(messages);
          }).catch((error) => {
            reject(error);
          });
        } catch (error) {
          console.error(error);
        }
      });
    },
    update_unread_counter: ({ state }, chat_id) => {
      return new Promise(resolve => {

        const currentChat = state.conversations.find(x => x.chat_id === chat_id);
        if (!currentChat || !currentChat.unread_counters) { resolve(); return; }

        // Throttle: skip if we just ran for this chat (coalesce bursts).
        const nowMs = Date.now();
        if (_unreadThrottle[chat_id] && (nowMs - _unreadThrottle[chat_id]) < 1200) { resolve(); return; }

        const myCounterKey = resolveCounterKey(state, currentChat);

        const totalMessagesReaded = currentChat.unread_counters[myCounterKey] || 0;
        if (totalMessagesReaded === 0) { resolve(); return; }
        _unreadThrottle[chat_id] = nowMs;

        const chatRef = database.collection("tenants").doc(state.tenant_id).collection("chats").doc(chat_id);

        // Zero MY unread counter for this chat. This is the only badge-related
        // write the client makes now: the updateNotifications trigger recomputes
        // the authoritative notifications.tenant_badge from unread_counters across
        // my chats and overwrites it. The client used to ALSO decrement
        // tenant_badge here from racy local state (currentChat.unread_counters),
        // which double-decremented against that trigger and wiped unread from
        // other chats (audit #8/#9) — removed.
        chatRef.set({ unread_counters: { [myCounterKey]: 0 } }, { merge: true })
          .then(() => {
            // Write read_statuses timestamps on unread messages from other users
            const now = new Date();
            const messagesRef = database.collection("tenants").doc(state.tenant_id)
              .collection("chats").doc(chat_id).collection("messages");
            const path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
            // Mark every unread message from other users as read. Bound the read
            // by the actual unread count (not the whole chat history); the prior
            // Math.min(..., 50) cap left messages 51+ without my read_status, so
            // chats with >50 unread showed wrong "Read" status for older messages.
            messagesRef.orderBy("creation_date", "desc").limit(totalMessagesReaded).get()
              .then(snap => {
                // Chunk writes at 500 (Firestore's per-batch limit) since the
                // unread count can exceed it. Same pattern as comms_mark_read.js.
                let readBatch = database.batch();
                let inBatch = 0;
                const commits = [];
                for (const doc of snap.docs) {
                  const msg = doc.data();
                  if (msg.sender_id !== path && (!msg.read_statuses || !msg.read_statuses[state.owner_id])) {
                    const update = {};
                    update[`read_statuses.${state.owner_id}`] = now;
                    readBatch.update(doc.ref, update);
                    inBatch++;
                    if (inBatch >= 500) {
                      commits.push(readBatch.commit());
                      readBatch = database.batch();
                      inBatch = 0;
                    }
                  }
                }
                if (inBatch > 0) commits.push(readBatch.commit());
                if (commits.length > 0) Promise.all(commits).catch(e => console.error('read_statuses write error:', e));
              })
              .catch(e => console.error('read_statuses query error:', e));
            resolve();
          })
          .catch(err => {
            console.error("Error updating unread counter:", err);
            resolve();
          });

      });
    },
    add_message: async function ({ commit, state, dispatch }, doc) {
      return new Promise(resolve => {
        let path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
        let xm = doc;
        xm.message_id = doc.id;

        if (xm.media) {
          if (xm.media.file_url) {
            xm.media_preview_url =
              xm.media.media_url =
              xm.media.media_preview_url = xm.media.file_url;

          } else {

            database
              .collection("tenants")
              .doc(state.tenant_id)
              .collection('files')
              .where('chat_id', "==", xm.chat_id)
              .where("message_id", "==", xm.message_id)
              .get().then(query => {
                commit('add_chat_media_file', query.docs);
                setTimeout(() => {
                  window.EventBus.$emit("onUpdateMessages")
                }, 100);
              }).catch(error => {
                console.error('[Chat] add_message_to_store media fetch failed:', error);
              });
          }
        }

        if (xm.message_replied) {
          dispatch('findMessage', xm);
        }

        let senderData = xm.sender_id.split("/");
        let sender = retrieveUser({ owner_id: senderData[4] }, state.uxDocs);
        if (sender) {
          xm.owner_id = sender.owner_id;
          xm.user_role = sender.user_role;
          xm.first_name = sender.first_name;
          xm.last_name = sender.last_name;
        }

        commit('addMessage', {
          auth_user: path,
          content: xm,
          empty: true
        });
        setTimeout(() => {
          window.EventBus.$emit("onUpdateMessages")
        }, 100);
        resolve();
      });
    },
    findMessage({ commit, state }, xm) {
      return new Promise(resolve => {
        database
          .collection("tenants")
          .doc(state.tenant_id)
          .collection('chats')
          .doc(xm.chat_id)
          .collection("messages")
          .doc(xm.message_replied)
          .get().then(messageRef => {
            if (!messageRef.exists) { resolve(); return; }
            let message = messageRef.data();
            message.message_id = messageRef.id;

            let senderData = message.sender_id.split("/");
            let sender = retrieveUser({ owner_id: senderData[4] }, state.uxDocs);
            if (sender) {
              message.owner_id = sender.owner_id;
              message.user_role = sender.user_role;
              message.first_name = sender.first_name;
              message.last_name = sender.last_name;
            }

            if (message.media) {
              database
                .collection("tenants")
                .doc(state.tenant_id)
                .collection('files')
                .where('chat_id', "==", message.chat_id)
                .where("message_id", "==", message.message_id)
                .get().then(query => {
                  for (const data of query.docs) {
                    let file = data.data();
                    if (file) {
                      if (isVideo(file)) {
                        loadImageFromVideo(file.url).then(videoPreview => {
                          const updateIndex = state.messages.findIndex(x => x.message_id === xm.message_id);
                          if (updateIndex !== -1) {
                            message.media_preview_url = videoPreview;

                            commit('add_chat_message_attribute', {
                              message_id: xm.message_id,
                              data: {
                                message_replied_data: getFormattedMessage(message, path)
                              }
                            });
                          }
                        });
                      } else if (isImage(file)) {
                        message.media_preview_url = file.url;
                      }
                    }
                  }

                  commit('add_chat_media_file', query.docs);
                }).catch(error => {
                  console.error('[Chat] findMessage media fetch failed:', error);
                });
            }

            let path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
            commit('add_chat_message_attribute', {
              message_id: xm.message_id,
              data: {
                message_replied_data: getFormattedMessage(message, path)
              }
            });

            setTimeout(() => {
              window.EventBus.$emit("onUpdateMessages")
            }, 100);
            resolve();
          }).catch(error => {
            console.error('[Chat] findMessage failed:', error);
            resolve();
          });
      });
    },
    messages: ({ commit, state, dispatch }, chat) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(resolve => {
        commit('status_retrieve', 'loading');
        if (!chat.chat_id) {
          commit('addMessages', {
            auth_user: null,
            content: [],
            empty: true
          });
          commit('status_retrieve', '');
          resolve([]);
        } else {
          // Race guard: remember the latest requested chat so a slow fetch for a
          // previously-opened chat can't overwrite the one now on screen.
          state.lastRequestedChatId = chat.chat_id;
          // Cache key is scoped by TENANT so a cached thread can never bleed into
          // another tenant after a tenant switch.
          const cacheKey = (state.tenant_id || '') + ':' + chat.chat_id;

          // Check cache first for instant switching
          if (state.messagesCache[cacheKey]) {
            state.messages = [...state.messagesCache[cacheKey]];
            commit('status_retrieve', '');
            // Still fetch fresh data in background to pick up new messages
            dispatch("pull_media", chat);
          }

          let path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
          let queryMessages = database
            .collection("tenants")
            .doc(state.tenant_id)
            .collection("chats")
            .doc(chat.chat_id)
            .collection('messages')
            .orderBy("creation_date", "desc")
            .limit(50);

          if (chat.chat && chat.chat.deleted && chat.chat.deleted[state.owner_id]) {
            queryMessages = queryMessages.where('creation_date', '>', new Date(chat.chat.deleted[state.owner_id]));
          }

          queryMessages.get()
            .then(collections => {
              let messages = [];
              for (const collection of collections.docs) {
                let xm = collection.data();
                if (!xm.sender_id) {
                  continue;
                }
                xm.message_id = collection.id;
                let senderData = xm.sender_id.split("/");
                let sender = retrieveUser({ owner_id: senderData[4] }, state.uxDocs);
                if (sender) {
                  xm.owner_id = sender.owner_id;
                  xm.user_role = sender.user_role;
                  xm.first_name = sender.first_name;
                  xm.last_name = sender.last_name;
                }

                if (xm.message_replied) {
                  dispatch('findMessage', xm);
                }

                messages.push(xm);
              }

              // Race guard: if the user has since switched to a different chat,
              // drop this stale result instead of clobbering the current thread.
              if (state.lastRequestedChatId !== chat.chat_id) {
                commit('status_retrieve', '');
                resolve();
                return;
              }
              commit('addMessages', {
                auth_user: path,
                content: messages,
                empty: true
              });
              // Cache the formatted messages (tenant-scoped key) + cap to ~20
              // chats so the cache can't grow without bound over a long session.
              state.messagesCache[cacheKey] = [...state.messages];
              const keys = Object.keys(state.messagesCache);
              if (keys.length > 20) delete state.messagesCache[keys[0]];
              dispatch("pull_media", chat);
              commit('status_retrieve', '');
              resolve();
            })
            .catch(error => {
              console.error('[Chat] retrieveMessages failed:', error);
              commit('status_retrieve', '');
              resolve();
            });
        }
      });
    },
    filesMessages: async function ({ commit, state }, user_id) {
      commit('status_retrieve', 'loading');
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve, reject) {
        let path = "";
        if (user_id) {
          path = "/tenants/" + state.tenant_id + "/users/" + user_id;
        } else {
          path = "/tenants/" + state.tenant_id + "/users/" + state.owner_id;
        }
        let response = [];
        let collections;
        if (user_id) {
          collections = await database
            .collection("tenants")
            .doc(state.tenant_id)
            .collection("files")
            .where("sender_id", "==", path)
            .orderBy('creation_date', 'desc')
            .limit(12)
            .get()
            .catch((error) => {
              console.error('[Chat] filesMessages failed:', error);
              commit('status_retrieve', '');
              reject(error);
              return;
            });
        } else {
          collections = await database
            .collection("tenants")
            .doc(state.tenant_id)
            .collection("files")
            .where("owner_ids", "array-contains", path)
            .orderBy('creation_date', 'desc')
            .limit(12)
            .get()
            .catch((error) => {
              console.error('[Chat] filesMessages failed:', error);
              commit('status_retrieve', '');
              reject(error);
              return;
            });
        }
        if (!collections) return;
        let files = [];
        for (const collection of collections.docs) {
          let xf = collection.data();
          let sender_id = xf.sender_id.split("/").pop();
          xf.user_data = retrieveUser({ owner_id: sender_id }, state.uxDocs);
          files.push(xf);
        }
        commit('addFiles', {
          files: files
        });

        commit('status_retrieve', '');
        resolve(response);
      })
    },
    pull_media: async function ({ commit, state }, chat) {
      return new Promise((resolve) => {
        if (chat) {
          database
            .collection("tenants")
            .doc(state.tenant_id)
            .collection('files')
            .where('chat_id', "==", chat.chat_id)
            .get().then(query => {
              commit('set_chat_media_files', query.docs);

              setTimeout(() => {
                window.EventBus.$emit("onUpdateMessages")
              }, 100);
            }).catch(error => {
              console.error('[Chat] pull_media failed:', error);
            });
          resolve();
        }
      });
    },
    add_contact: function ({ commit }, contact) {
      commit('status_contact', 'sending');
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        try {
          await axios.post(config.api_url + 'contacts', contact);
          commit('status_contact', '');
          resolve('success');
        } catch (error) {
          commit('status_contact', '');
          console.error(error);
        }
      });
    },
    remove_participant: function ({ commit }, { chat_id, user_id }) {
      commit('status_chat', 'loading');
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        try {
          await axios.post(config.api_url + 'chat/remove_participant', { chat_id, user_id });
          commit('status_chat', '');
          resolve('success');
        } catch (error) {
          commit('status_chat', '');
          console.error(error);
          resolve();
        }
      });
    },
    leave_group: function ({ commit }, payload) {
      const self = this;
      commit('status_chat', 'loading');
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        try {
          await axios.post(config.api_url + 'chat/leave_group', { chat_id: payload.chat_id });
          self.selectedChatId = "";
          commit('status_chat', '');
          resolve('success');
        } catch (error) {
          commit('status_chat', '');
          console.error(error);
        }
      });
    },
    edit_group: function ({ commit }, options) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_chat', 'loading');
        try {
          await axios.post(config.api_url + 'chat/edit_group', options);
        } catch (error) {
          commit('status_chat', '');
          console.error(error);
        }
        commit('status_chat', '');
        resolve('success');
      });
    },
    delete_conversation: function ({ commit }, chat_id) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_chat', 'loading');
        commit('status_conversations', 'loading');
        try {
          await axios.delete(config.api_url + 'delete_conversation?user_conversation_id=' + chat_id);
        } catch (error) {
          commit('status_chat', '');
          commit('status_conversations', '');
          console.error(error);
        }

        commit('status_chat', '');
        commit('status_conversations', '');
        resolve('success');
      });
    },
    mute_group: function ({ commit }, params) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_chat', 'loading');
        try {
          await axios.post(config.api_url + 'chat/mute_group', {
            chat_id: params.chat_id,
            is_mute: params.isMute
          });
        } catch (error) {
          commit('status_chat', '');
          console.error(error);
        }
        commit('status_chat', '');
        resolve('success');
      });
    },
    send_message: function ({ state, dispatch }, data) {
      let message = {
        ...data,
        is_from_web: true
      };
      if (message[message.type] && Object.keys(message[message.type]).length > 0 && message[message.type] instanceof Object) {
        message = {
          ...message[message.type],
          type: message.type,
          chat_id: message.chat_id,
          message_identifier: message.message_identifier,
          fowarded: message.fowarded
        };
      }
      if (!data.message_identifier) {
        message.message_identifier = uuid();
      }

      let localMessage = {
        sending: true,
        sending_status: "sending",
        sender_id: `/tenants/${state.tenant_id}/users/${state.owner_id}`,
        message_identifier: message.message_identifier,
        id: message.message_id ? message.message_id : message.message_identifier,
        message_id: message.message_id ? message.message_id : message.message_identifier,
        creation_date: {
          toDate: () => message.creation_date ? new Date(message.creation_date) : new Date()
        },
        fowarded: message.fowarded
      };

      let serverSending = [];

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        try {
          switch (message.type) {
            case "text":
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_text', {
                  chat_id: message.chat_id,
                  message_identifier: message.message_identifier,
                  text: message.text,
                  type: message.type,
                  message_replied: message.message_replied,
                  message_id: message.message_id ? message.message_id : undefined,
                  fowarded: message.fowarded
                })
              });
              break;
            case "address":
              localMessage = {
                ...localMessage,
                address: message
              };
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_address', message)
              });
              break;
            case "event":
              localMessage = {
                ...localMessage,
                event: {
                  ...message,
                  localMessage,
                  start_date: {
                    toDate: () => {
                      return message.start_date
                    }
                  },
                  end_date: {
                    toDate: () => {
                      return message.end_date
                    }
                  }
                },
                type: "event"
              };
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_event', message)
              });
              break;
            case "update":
              localMessage = {
                ...localMessage,
                update: message
              };
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_update', message)
              });
              break;
            case "invoice":
              localMessage = {
                ...localMessage,
                invoice: message,
                sending_status: "generated_pdf"
              };
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_invoice', message)
              });
              localMessage = {
                ...localMessage,
                invoice: message
              };
              break;
            case "link":
              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_link', message)
              });
              break;
            case "media":
              localMessage = {
                ...localMessage,
                media: message
              };
              if (!message.file_url && message.file) {
                uploadFileToStorage(message.file, 'messages', state.tenant_id).then((url) => {
                  message = {
                    ...message,
                    file_url: url
                  };

                  const fileTmp = {
                    url: url,
                    type: message.file?.type  // si existe, para detectar MIME real
                  };
                  if (isImage(fileTmp)) {
                    message.file_type = "media";
                    message.file_subtype = "photo";
                  } else if (isVideo(fileTmp)) {
                    message.file_type = "media";
                    message.file_subtype = "video";
                  } else {
                    message.file_type = "doc";
                    message.file_subtype = "doc";
                  }

                  delete message.file;
                  localMessage = {
                    ...localMessage,
                    media: message
                  };

                  const sendMedia = (retries) => {
                    axios
                      .post(config.api_url + 'chats/send_media', message)
                      .catch(() => {
                        if (retries > 0) {
                          setTimeout(() => sendMedia(retries - 1), 2000);
                        } else {
                          message = { ...message, ...localMessage, sending_status: "error" };
                          dispatch("add_message", message);
                        }
                      });
                  };
                  sendMedia(2);

                }).catch(() => {
                  // Retry upload once after 2s
                  setTimeout(() => {
                    uploadFileToStorage(message.file, 'messages', state.tenant_id).then((url) => {
                      message = { ...message, file_url: url };
                      delete message.file;
                      axios.post(config.api_url + 'chats/send_media', message).catch(() => {
                        dispatch("add_message", { ...message, ...localMessage, sending_status: "error" });
                      });
                    }).catch(() => {
                      dispatch("add_message", { ...message, ...localMessage, sending_status: "error" });
                    });
                  }, 2000);
                });
              } else {

                serverSending.push({
                  message,
                  localMessage,
                  request: axios.post(config.api_url + 'chats/send_media', message)
                });
              }
              break;
            case "ontheway":

              if (localMessage.message_id === localMessage.message_identifier) {
                localMessage = {
                  ...localMessage,
                  ontheway: {
                    address_message_id: message.address_message_id,
                    description: message.address,
                    estimated_time_arrival: {
                      toDate() {
                        return message.estimated_time_arrival
                      }
                    },
                    latitude: message.latitude,
                    longitude: message.longitude
                  }
                };
              }

              serverSending.push({
                message,
                localMessage,
                request: axios.post(config.api_url + 'chats/send_ontheway', message)
              });
              break;
            case "contact":
              serverSending = [];
              if (message.contacts) {
                for (let contact of message.contacts) {

                  contact = {
                    ...contact,
                    message_identifier: uuid(),
                  };

                  localMessage = {
                    ...localMessage,
                    chat_id: contact.chat_id,
                    id: contact.message_identifier,
                    message_id: contact.message_identifier,
                    message_identifier: contact.message_identifier,
                    type: "contact",
                    contact: state.uxDocs[contact.user_id],
                  };

                  dispatch("add_message", localMessage);
                  serverSending.push(
                    {
                      message: contact,
                      localMessage,
                      request: axios.post(config.api_url + 'chats/send_contact', contact)
                    }
                  );
                }
              }

              if (data.contact && data.contact.owner_id) {
                localMessage = {
                  ...localMessage,
                  chat_id: data.chat_id,
                  id: data.message_identifier,
                  message_id: data.message_identifier,
                  message_identifier: data.message_identifier,
                  type: "contact",
                  contact: data.contact,
                };

                dispatch("add_message", localMessage);
                let contact = {
                  chat_id: message.chat_id,
                  user_id: data.contact.owner_id,
                  message_identifier: data.message_identifier,
                  fowarded: message.fowarded
                };
                serverSending.push(
                  {
                    message: message.contact,
                    localMessage,
                    request: axios.post(config.api_url + 'chats/send_contact', contact)
                  }
                );
              }

              break;
          }
        } catch (error) {
          //
        }

        if (message.type !== "contact") {
          localMessage = {
            ...localMessage,
            ...message,
            id: message.message_identifier,
            message_id: localMessage.message_id,
          };
          dispatch("add_message", localMessage);
        }

        // Optimistically update sidebar last_message so timestamp is current
        const convIndex = state.conversations.findIndex(x => x.chat_id === message.chat_id);
        if (convIndex !== -1) {
          const conv = state.conversations[convIndex];
          conv.last_message = {
            ...conv.last_message,
            date: { toDate: () => new Date() },
            type: message.type || 'text',
            content: message.text || message.type || '',
            sender: {
              id: `/tenants/${state.tenant_id}/users/${state.owner_id}`,
              first_name: state.logged.first_name,
              last_name: state.logged.last_name,
            },
            is_from_web: true,
          };
        }

        resolve(true);

        if (serverSending.length > 0) {
          for (const sending of serverSending) {
            await sending.request
              .then((response) => {
                if (response && response.status === 200) {
                  let message = {
                    ...sending.message,
                    ...sending.localMessage,
                    sending: false,
                    sending_status: "success",
                    message_id: response.data.message_id,
                    id: response.data.message_id
                  };
                  dispatch("add_message", message);
                }
              })
              .catch(() => {
                let message = {
                  ...sending.message,
                  ...sending.localMessage,
                  sending_status: "error"
                };
                dispatch("add_message", message);
                // Surface a visible toast so the user knows it didn't go out
                // (the bubble also shows a tap-to-retry error state).
                try { window.EventBus.$emit("message-send-failed"); } catch (e) { /* no bus */ }
              });
          }
        }
      });
    },
    // eslint-disable-next-line no-empty-pattern
    reaction: ({ }, reaction) => {
      axios.post(config.api_url + 'chats/reaction', reaction).catch(err => {
        console.error('[Chat] reaction failed:', err);
      });
    },
    // eslint-disable-next-line no-empty-pattern
    delete_message: ({ }, message) => {
      axios.delete(config.api_url + 'delete_message', {
        params: {
          chat_id: message.chat_id,
          user_message_id: message.message_id
        }
      });
    },
    send_notification: async function ({ commit }, data) {
      commit('status_notification', 'sending');
      try {
        await axios.post(config.api_url + 'send_push_notifications', data);
      } catch (error) {
        console.error(error);
      }
      commit('status_notification', '');
    },
    // eslint-disable-next-line no-empty-pattern
    register_device: async function ({ commit }, data) {
      return new Promise(resolve => {
        commit('device_token', data.device_token);
        axios.post(config.api_url + 'register_device_token', data).then(() => {
          resolve();
        }).catch(error => {
          console.error(error);
          resolve();
        })
      });
    },
    // eslint-disable-next-line no-empty-pattern
    connect_device: async function ({ }, data) {
      return new Promise(resolve => {
        axios.post(config.api_url + 'connect_device', data).then(() => {
          resolve();
        }).catch(error => {
          console.error(error);
          resolve();
        })
      });
    },
    get_chat: function ({ commit, state }, params) {
      let { participant_ids = [], chat_mode = 'single' } = params;
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_retrieve', 'loading');
        let path = "/tenants/" + state.tenant_id + "/users/";
        participant_ids.push(state.logged['owner_id']);
        for (const p in participant_ids) {
          participant_ids[p] = path + participant_ids[p];
        }

        let chat_id = null;
        for (const c of state.conversations) {
          let p_dis = c.participant_ids;
          let countPIds = 0;
          for (const toCheck of participant_ids) {
            if (p_dis.findIndex(a => a === toCheck) !== -1) {
              countPIds++;
            }
          }
          if (countPIds === participant_ids.length && countPIds === p_dis.length) {
            if (c.removed_users.length === 0 || !c.removed_users) {
              chat_id = c.chat_id;

              if (chat_mode === 'single' && c.is_group) {
                chat_id = null;
              }

              if (chat_mode === 'group' && !c.is_group) {
                chat_id = null;
              }
            }
            break;
          }
        }
        resolve(chat_id);
        commit('status_retrieve', '');
      });
    },
    new_chat: function ({ commit, state }, chat) {
      //eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_chat', 'loading');
        commit('status_conversations', 'loading');
        try {
          let participants = [];
          for (const participant of chat.participants) {
            participants.push({
              first_name: participant['first_name'],
              last_name: participant['last_name'],
              phone_number: participant['phone_number'],
              user_id: participant['user_id']
            })
          }
          participants.push({
            first_name: state.logged['first_name'],
            last_name: state.logged['last_name'],
            phone_number: state.logged['phone_number'],
            user_id: state.logged['owner_id']
          });

          chat.participants = participants;

          let participant_ids = [];
          for (const prop in participants) {
            if (participants[prop]['user_id'] !== state.logged['owner_id']) {
              participant_ids.push(participants[prop]['user_id']);
            }
          }

          let conversation;
          await axios.post(config.api_url + 'chats', chat).then(response => {
            let attempts = 0;
            // Cap the wait at ~4s (was 7.5s). The conversations listener almost
            // always delivers the new chat in well under a second; this only
            // bounds the rare miss before we fall back to a stub.
            const maxAttempts = 8;
            let id = setInterval(() => {
              attempts++;
              conversation = state.conversations.find(x => x.chat_id === response.data.chat_id);
              if (conversation) {
                clearInterval(id);
                resolve(conversation);
              } else if (attempts >= maxAttempts) {
                clearInterval(id);
                // Resolve with a minimal conversation object so the UI doesn't hang
                resolve({ chat_id: response.data.chat_id, ...(response.data || {}) });
              }
            }, 500);
          });

        } catch (error) {
          console.error(error);
          commit('status_chat', '');
          commit('status_conversations', '');
        }

        commit('status_chat', '');
        commit('status_conversations', '');
      });
    },
    users: async function ({ commit }) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_users', 'filling');

        commit('addUsers');
        resolve('success');

        commit('status_users', '');
      });
    },
    logged: async function ({ commit, state }) {
      return new Promise(resolve => {
        try {
          let queryDB;
          database.collection("users")
            .doc(state['owner_id'])
            .get().then(query_global => {
              if (state['user_doc'] === state['tenant_id']) {
                //is owner
                queryDB = database.collection('tenants').doc(state['tenant_id']);
              } else {
                //has owner
                queryDB = database.collection('tenants').doc(state['tenant_id']).collection('users').doc(state['user_doc']);
              }

              // Fetch tenant doc for company_name alongside user/tenant query
              const tenantDocPromise = database.collection('tenants').doc(state['tenant_id']).get();

              Promise.all([queryDB.get(), tenantDocPromise]).then(([query, tenantDoc]) => {
                let user_global = query_global.data();
                let user_details = query.data();
                user_details['id'] = query.id;

                for (let key in user_global) {
                  user_details[key] = user_global[key];
                }

                // Set tenant company_name from the tenant doc (not the user's global doc)
                if (tenantDoc.exists && tenantDoc.data().company_name) {
                  commit('tenant_company_name', tenantDoc.data().company_name);
                }

                // Conditional on purpose — see the note in views/chat/chat.js.
                // Assigning unconditionally nulled the shared axios default
                // whenever `auth_id` was absent (e.g. an admin-panel session,
                // which stores its token under `auth_token`).
                const loggedAuthId = localStorage.getItem('auth_id');
                if (loggedAuthId) {
                  axios.defaults.headers.common['Authorization'] = loggedAuthId;
                }
                commit('logged', user_details);
                resolve();
              }).catch(error => {
                console.error('[Chat] logged inner queries failed:', error);
                resolve();
              });
            }).catch(error => {
              console.error('[Chat] logged user doc fetch failed:', error);
              resolve();
            });

        } catch (error) {
          console.error(error);
          resolve();
        }
      });
    },

    retrieve_user: function ({ commit }, user_id) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve) {
        commit('status_retrieve', 'loading');
        try {
          database
            .collection('users')
            .doc(user_id)
            .get()
            .then((doc) => {
              if (doc.exists) {
                resolve(doc.data());
              } else {
                resolve(null);
              }
              commit('status_retrieve', '');
            })
            .catch(error => {
              console.error('[Chat] retrieve_user failed:', error);
              commit('status_retrieve', '');
              resolve(null);
            });
        } catch (error) {
          console.error(error);
          commit('status_retrieve', '');
          resolve(null);
        }
      });
    }
  },
  getters: {
    getToken(state) {
      return state.token;
    },
    isLoggedIn(state) {
      return state.owner_id;
    }
  },
};
